/**
 * Temu 定制品工艺：productSaleExtAttrReq.customizedTechnologyReq
 * 层级来源：官方「定制品定制工艺层级关系」（按 technologyType → firstType → twiceType）。
 *
 * 说明：personalizationSwitch（是否定制品）与定制工艺是两套概念。
 * 定制工艺仅特殊类目需要，选定制品时不必强制填写。
 */

import { fixedUi } from '@/i18n/fixedUi';
import { resolveTemuRegion1ShortName } from '@/temu-platform/utils/temuProductOrigin';

/** 一级工艺 */
export const TEMU_CUSTOM_FIRST_TYPE_OPTIONS = Object.freeze([
  { value: '10001', label: '基础工艺' },
  { value: '10002', label: '木制品定制工艺' },
  { value: '10003', label: '金属制品定制工艺' },
  { value: '10004', label: '皮具/布艺定制工艺' },
  { value: '10005', label: '有机材料（亚克力/树脂等）定制工艺' },
  { value: '10006', label: '画布/纸张定制工艺' },
]);

/**
 * 二级工艺（官方枚举 + 层级表中出现的补充码）。
 * 20028–20032 出现在画布/纸张单一工艺层级中，公开枚举文案未给出名称。
 */
export const TEMU_CUSTOM_TWICE_TYPE_OPTIONS = Object.freeze([
  { value: '20001', label: '激光雕刻-文字' },
  { value: '20002', label: '激光雕刻-图片' },
  { value: '20003', label: '机械雕刻-文字' },
  { value: '20004', label: '机械雕刻-图片' },
  { value: '20005', label: '焊接' },
  { value: '20006', label: '蚀刻' },
  { value: '20007', label: 'UV' },
  { value: '20008', label: '丝网印刷-文字' },
  { value: '20009', label: '丝网印刷-图片' },
  { value: '20010', label: '喷漆' },
  { value: '20011', label: '滴油' },
  { value: '20012', label: '滴胶-文字' },
  { value: '20013', label: '滴胶-图片' },
  { value: '20014', label: '激光切割-文字' },
  { value: '20015', label: '激光切割-图片' },
  { value: '20016', label: '镶钻' },
  { value: '20017', label: '烫金-文字' },
  { value: '20018', label: '烫金-图片' },
  { value: '20019', label: '锡金' },
  { value: '20020', label: '热转印-文字' },
  { value: '20021', label: '热转印-图片' },
  { value: '20022', label: '刺绣' },
  { value: '20023', label: '植绒' },
  { value: '20024', label: '电镀' },
  { value: '20025', label: '烫画-文字' },
  { value: '20026', label: '烫画-图片' },
  { value: '20027', label: '数码直喷' },
  { value: '20028', label: '二级工艺 20028' },
  { value: '20029', label: '二级工艺 20029' },
  { value: '20030', label: '二级工艺 20030' },
  { value: '20031', label: '二级工艺 20031' },
  { value: '20032', label: '二级工艺 20032' },
  { value: '20033', label: '压印' },
  { value: '20034', label: '美甲手绘' },
  { value: '20035', label: '机织图案-胶贴' },
  { value: '20036', label: '烫钻' },
  { value: '20037', label: '手绘' },
  { value: '20038', label: '水转印印刷' },
  { value: '20039', label: '烤漆' },
  { value: '20040', label: '3D打印' },
]);

/** 工艺类型：1 单一 / 2 组合（与官方层级 JSON 顶层 key 一致） */
export const TEMU_CUSTOM_TECHNOLOGY_TYPE_OPTIONS = Object.freeze([
  { value: '1', label: '单一工艺' },
  { value: '2', label: '组合工艺' },
]);

/**
 * technologyType → firstType → twiceType[]
 * 10001 基础工艺仅出现在单一工艺，且二级为空。
 */
export const TEMU_CUSTOM_TECH_HIERARCHY = Object.freeze({
  1: Object.freeze({
    10001: Object.freeze([]),
    10002: Object.freeze([
      20001, 20002, 20003, 20004, 20007, 20008, 20009, 20010, 20020, 20021, 20025, 20026, 20037,
    ]),
    10003: Object.freeze([
      20001, 20002, 20003, 20004, 20005, 20006, 20007, 20008, 20009, 20010, 20011, 20012,
      20013, 20014, 20015, 20016, 20020, 20021, 20024, 20036, 20039,
    ]),
    10004: Object.freeze([
      20001, 20002, 20007, 20008, 20009, 20011, 20012, 20013, 20016, 20017, 20018, 20019,
      20020, 20021, 20022, 20023, 20025, 20026, 20027, 20033, 20035, 20036, 20037,
    ]),
    10005: Object.freeze([
      20001, 20002, 20003, 20004, 20007, 20008, 20009, 20014, 20015, 20016, 20034, 20036,
      20038, 20040,
    ]),
    10006: Object.freeze([
      20007, 20008, 20009, 20017, 20018, 20028, 20029, 20030, 20031, 20032, 20033, 20037,
    ]),
  }),
  2: Object.freeze({
    10002: Object.freeze([
      20001, 20002, 20003, 20004, 20007, 20008, 20009, 20010, 20020, 20021, 20025, 20026, 20037,
    ]),
    10003: Object.freeze([
      20001, 20002, 20003, 20004, 20005, 20006, 20007, 20008, 20009, 20010, 20011, 20012,
      20013, 20014, 20015, 20016, 20020, 20021, 20024, 20036, 20039,
    ]),
    10004: Object.freeze([
      20001, 20002, 20007, 20008, 20009, 20011, 20012, 20013, 20016, 20017, 20018, 20019,
      20020, 20021, 20022, 20023, 20025, 20026, 20027, 20033, 20035, 20036, 20037,
    ]),
    10005: Object.freeze([
      20001, 20002, 20003, 20004, 20007, 20008, 20009, 20014, 20015, 20016, 20034, 20036, 20038, 20040,
    ]),
    10006: Object.freeze([20007, 20008, 20009, 20017, 20018, 20033, 20037]),
  }),
});

const FIRST_TYPE_SET = new Set(TEMU_CUSTOM_FIRST_TYPE_OPTIONS.map((o) => o.value));
const TWICE_TYPE_SET = new Set(TEMU_CUSTOM_TWICE_TYPE_OPTIONS.map((o) => o.value));
const TWICE_LABEL_BY_ID = new Map(TEMU_CUSTOM_TWICE_TYPE_OPTIONS.map((o) => [o.value, o.label]));
const FIRST_LABEL_BY_ID = new Map(TEMU_CUSTOM_FIRST_TYPE_OPTIONS.map((o) => [o.value, o.label]));

function toPositiveInt(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.trunc(n);
}

/**
 * @param {unknown} raw
 * @returns {boolean}
 */
export function isTemuCustomProductEnabled(raw) {
  if (raw == null) return false;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'object') {
    return isTemuCustomProductEnabled(
      raw.isCustomProduct
      ?? raw.personalizationSwitch
      ?? raw.customProduct,
    );
  }
  const text = String(raw).trim().toLowerCase();
  if (!text) return false;
  if (text === '1' || text === 'true' || text === 'custom' || text === '定制品') return true;
  if (text === '0' || text === 'false' || text === 'regular' || text === '普通品') return false;
  return Number(text) === 1;
}

/**
 * @param {unknown} technologyType
 * @returns {number[]}
 */
export function listTemuCustomFirstTypeIds(technologyType) {
  const type = toPositiveInt(technologyType);
  const bucket = TEMU_CUSTOM_TECH_HIERARCHY[type];
  if (!bucket) return [];
  return Object.keys(bucket).map((id) => Number(id)).filter((id) => id > 0);
}

/**
 * @param {unknown} technologyType
 * @returns {{ value: string, label: string }[]}
 */
export function listTemuCustomFirstTypeOptions(technologyType) {
  return listTemuCustomFirstTypeIds(technologyType).map((id) => ({
    value: String(id),
    label: fixedUi(FIRST_LABEL_BY_ID.get(String(id)) || '') || `${fixedUi('一级工艺')} ${id}`,
  }));
}

/**
 * @param {unknown} technologyType
 * @param {unknown} firstType
 * @returns {number[]|null} null=层级未就绪；[]=该一级下无二级（如基础工艺）
 */
export function listTemuCustomTwiceTypeIds(technologyType, firstType) {
  const type = toPositiveInt(technologyType);
  const first = toPositiveInt(firstType);
  const bucket = TEMU_CUSTOM_TECH_HIERARCHY[type];
  if (!bucket || !first) return null;
  if (!Object.prototype.hasOwnProperty.call(bucket, String(first))
    && !Object.prototype.hasOwnProperty.call(bucket, first)) {
    return null;
  }
  const list = bucket[first] || bucket[String(first)] || [];
  return Array.isArray(list) ? list.slice() : [];
}

/**
 * @param {unknown} technologyType
 * @param {unknown} firstType
 * @returns {{ value: string, label: string }[]}
 */
export function listTemuCustomTwiceTypeOptions(technologyType, firstType) {
  const ids = listTemuCustomTwiceTypeIds(technologyType, firstType);
  if (!ids) return [];
  return ids.map((id) => ({
    value: String(id),
    label: fixedUi(TWICE_LABEL_BY_ID.get(String(id)) || '') || `${fixedUi('二级工艺')} ${id}`,
  }));
}

/**
 * @param {unknown} raw
 * @param {Set<string>|null} [allowSet] 若传入则只保留层级内 id
 * @returns {number[]}
 */
export function normalizeTemuTwiceTypes(raw, allowSet = null) {
  const list = Array.isArray(raw)
    ? raw
    : (raw == null || raw === '' ? [] : [raw]);
  const out = [];
  const seen = new Set();
  list.forEach((item) => {
    const id = toPositiveInt(item);
    const key = String(id);
    if (!id || !TWICE_TYPE_SET.has(key) || seen.has(id)) return;
    if (allowSet && !allowSet.has(key)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
}

/**
 * 制造地点：{@code manufacturingLocation.region1ShortNames}（二字区域码列表，去重）。
 * 官方文档：temu-货品API组PA.md「manufacturingLocation - Manufacturing Location」。
 * 只下发二字简码；中文名（如「中国大陆」）会先映射成 CN。
 * @param {unknown} raw
 * @returns {string[]}
 */
export function normalizeTemuManufacturingLocations(raw) {
  const list = Array.isArray(raw) ? raw : (raw == null || raw === '' ? [] : [raw]);
  const out = [];
  const seen = new Set();
  list.forEach((item) => {
    const code = resolveTemuRegion1ShortName(item);
    if (!/^[A-Z]{2}$/.test(code) || seen.has(code)) return;
    seen.add(code);
    out.push(code);
  });
  return out;
}

/**
 * @param {object} [form]
 * @returns {{ firstType: string, technologyType: string, twiceTypes: string[], manufacturingLocations: string[] }}
 */
export function emptyTemuCustomizedTechnology(form = {}) {
  const nested = form.productSaleExtAttrReq?.customizedTechnologyReq;
  const raw = (nested && typeof nested === 'object')
    ? nested
    : (form.customizedTechnologyReq && typeof form.customizedTechnologyReq === 'object'
      ? form.customizedTechnologyReq
      : form);
  const firstType = String(raw.firstType ?? form.customFirstType ?? '').trim();
  const technologyType = String(raw.technologyType ?? form.customTechnologyType ?? '').trim();
  const allowed = listTemuCustomTwiceTypeIds(technologyType, firstType);
  const allowSet = allowed ? new Set(allowed.map(String)) : null;
  const twiceTypes = normalizeTemuTwiceTypes(
    raw.twiceType ?? raw.twiceTypes ?? form.customTwiceTypes ?? form.twiceType,
    allowSet,
  ).map(String);
  const manufacturingLocations = normalizeTemuManufacturingLocations(
    raw.manufacturingLocation?.region1ShortNames
    ?? raw.manufacturingLocations
    ?? form.customManufacturingLocations,
  );
  return {
    firstType: FIRST_TYPE_SET.has(firstType) ? firstType : '',
    technologyType: technologyType === '1' || technologyType === '2' ? technologyType : '',
    twiceTypes,
    manufacturingLocations,
  };
}

/**
 * 用户是否开始填写定制工艺（任一字段有值）。
 * @param {object} [form]
 * @returns {boolean}
 */
export function hasTemuCustomizedTechnologyInput(form = {}) {
  const normalized = emptyTemuCustomizedTechnology(form);
  return Boolean(
    normalized.technologyType
    || normalized.firstType
    || (normalized.twiceTypes && normalized.twiceTypes.length),
  );
}

/**
 * 制造地点：全托管必填、半托管选填。有二字区域码才返回对象，未填返回 null（调用方不得下发空对象）。
 * @param {object} [form]
 * @returns {{ region1ShortNames: string[] }|null}
 */
export function buildTemuManufacturingLocation(form = {}) {
  const names = emptyTemuCustomizedTechnology(form).manufacturingLocations;
  if (!names.length) return null;
  return { region1ShortNames: names };
}

/**
 * 组装 productSaleExtAttrReq.customizedTechnologyReq。
 * - 普通品：null
 * - 定制品但未填工艺：null（工艺仅特殊类目需要）
 * - 已填工艺：按官方层级校验后返回
 * @param {object} [form]
 * @returns {{ firstType: number, technologyType: number, twiceType?: number[] }|null}
 */
export function buildTemuCustomizedTechnologyReq(form = {}) {
  if (!isTemuCustomProductEnabled(form)) return null;
  if (!hasTemuCustomizedTechnologyInput(form)) return null;

  const normalized = emptyTemuCustomizedTechnology(form);
  const firstType = toPositiveInt(normalized.firstType);
  const technologyType = toPositiveInt(normalized.technologyType);
  const allowedIds = listTemuCustomTwiceTypeIds(technologyType, firstType);
  const allowSet = allowedIds ? new Set(allowedIds.map(String)) : null;
  const twiceType = normalizeTemuTwiceTypes(normalized.twiceTypes, allowSet);
  // 制造地点（法国等站点合规必填）：以用户填写为准
  const manufacturingLocation = buildTemuManufacturingLocation(form);

  if (technologyType !== 1 && technologyType !== 2) {
    throw new Error('请选择工艺类型（单一/组合）');
  }
  if (!firstType || !FIRST_TYPE_SET.has(String(firstType))) {
    throw new Error('请选择一级工艺');
  }
  if (!allowedIds) {
    throw new Error('当前工艺类型下不可选该一级工艺');
  }
  // 基础工艺（单一）：二级为空，不传 twiceType
  if (allowedIds.length === 0) {
    if (twiceType.length) {
      throw new Error('基础工艺无需选择二级工艺');
    }
    const base = { firstType, technologyType };
    if (manufacturingLocation) base.manufacturingLocation = manufacturingLocation;
    return base;
  }
  if (technologyType === 1 && twiceType.length !== 1) {
    throw new Error('单一工艺请选择且仅选择 1 项二级工艺');
  }
  if (technologyType === 2 && twiceType.length < 2) {
    throw new Error('组合工艺请至少选择 2 项二级工艺');
  }
  const req = {
    firstType,
    technologyType,
    twiceType,
  };
  if (manufacturingLocation) req.manufacturingLocation = manufacturingLocation;
  return req;
}

/**
 * @param {object} [form]
 * @returns {{ ok: true }|{ ok: false, message: string, field: string }}
 */
export function validateTemuCustomizedTechnology(form = {}) {
  if (!isTemuCustomProductEnabled(form)) {
    return { ok: true };
  }
  // 定制品可不填工艺；只有开始填写时才校验完整性与层级
  if (!hasTemuCustomizedTechnologyInput(form)) {
    return { ok: true };
  }
  try {
    buildTemuCustomizedTechnologyReq(form);
    return { ok: true };
  } catch (err) {
    const message = String(err?.message || '定制工艺不完整');
    let field = 'customFirstType';
    if (/工艺类型|单一|组合/.test(message)) field = 'customTechnologyType';
    else if (/二级工艺|基础工艺无需/.test(message)) field = 'customTwiceTypes';
    return { ok: false, message, field };
  }
}
